import React from 'react';
import styled from 'styled-components';
import { SPWMainButton } from 'spw-shared/globalComponents';
import { Box, Container, Grid, IconButton, TextField } from '@mui/material';
import { colors } from 'spw-theme';
import { GridProps } from '@mui/material/Grid';
import DeleteIcon from '@mui/icons-material/Delete';
import { Link } from 'react-router-dom-v5';
import { SPWButtonAsText } from 'spw-shared/globalComponents/buttons';
import { HEADER_HEIGHT_IN_PX } from 'spw-components/header/styles';

export const DownloadsTitle = styled.h1`
  font-family: 'Roboto';
  font-style: normal;
  font-weight: 300;
  font-size: 32px;
  line-height: 34px;
  color: ${colors.dark};
  margin: 0px 0px 24px;
`;

export const EmptyListBox = styled(Box)`
  font-family: 'Roboto';
  font-style: normal;
  font-weight: 400;
  font-size: 14px;
  line-height: 20px;
  padding: 18px 16px;
`;

export const TemplatesContainer = styled(Container)`
  && {
    padding-left: 0px;
  }
`;

export const TemplatesListContainer = styled(Box)`
  && {
    margin-top: 16px;
  }
`;

export const TemplateCardBox = styled(Box)`
  && {
    border: 1px solid ${colors.veryLightGray};
    border-radius: 8px;
    box-sizing: border-box;
    height: 82px;
    padding: 16px;

    &:hover {
      background: ${colors.backgroundGrey};
      cursor: pointer;
    }
  }
`;

export const TemplateNameInput = styled(TextField)`
  && {
    padding: 0px;
    background-color: ${colors.white};
    width: calc(100% - 26px);

    &.MuiFormControl-root {
      border-radius: 4px;
    }

    div.MuiInputBase-root.MuiOutlinedInput-root.MuiInputBase-formControl {
      font-size: 16px;
      line-height: 16px;
      letter-spacing: 0.25px;
      height: 28px;

      :focus {
        border: 1px solid ${colors.lightGreyBackground};
      }
    }

    input {
      font-size: 16px;
      padding: 2px 4px 2px 4px;
    }
  }
`;

export const DateAndNumberOfFilesRow = styled(Box)`
  && {
    margin-top: 8px;
    display: flex;
    font-weight: 400;
    font-size: 12px;
    line-height: 16px;
    color: ${colors.fontColor};
  }
`;

export const UserName = styled(Box)`
  && {
    font-weight: 400;
    font-size: 12px;
    line-height: 16px;
    color: ${colors.fontColor};
  }
`;

export const DownloadButton = styled.img`
  && {
    width: 14px;
    height: 14px;
    cursor: pointer;
  }
`;

export const DownloadOptionsButton = styled(SPWMainButton)`
  && {
    padding: 8px 14px;
  }
`;

export const HistoryPanelContainer = styled((props: GridProps) => (
  <Grid item xs={12} {...props} />
))`
  && {
    padding-top: 24px;
  }
`;

export const DownloadsHistoryPanel = styled(Box)`
  min-width: 390px;
  border-radius: 8px;
  border: 1px solid ${colors.veryLightGray};
  min-height: 536px;
`;

export const DownloadsHistoryHeader = styled(Box)`
  padding: 16px;
  font-style: normal;
  font-weight: 400;
  font-size: 12px;
  line-height: 16px;
  color: ${colors.mainGray};
  box-sizing: border-box;
  height: 72px;
  border-bottom: 1px solid ${colors.offWhite};
`;

export const PanelRowTitle = styled.p`
  font-family: 'Roboto';
  font-style: normal;
  font-weight: 500;
  font-size: 16px;
  line-height: 20px;
  color: ${colors.black};
  margin: 0px 0px 4px 0px;
`;

export const DownloadsList = styled(Box)`
  && {
    max-height: 448px;
    overflow-y: scroll;
  }
`;

export const HistoryRow = styled(Box)`
  padding: 8px 16px;
  font-style: normal;
  font-weight: 400;
  font-size: 12px;
  line-height: 16px;
  color: ${colors.mainGray};
  box-sizing: border-box;
  height: 56px;
  border-bottom: 1px solid ${colors.offWhite};

  &&:hover {
    background: ${colors.backgroundGrey};
  }
`;

export const DownloadName = styled(Box)`
  && {
    font-weight: 400;
    font-size: 14px;
    line-height: 20px;
    color: ${colors.black};
  }
`;

export const DeleteDownloadIconButton = styled(IconButton)`
  && {
    width: 14px;
    height: 14px;
    padding-right: 10px;
  }
`;

export const DeleteDownloadIcon = styled(DeleteIcon)`
  && {
    width: 14px;
    height: 14px;
  }
`;

export const TemplateDetailsContainer = styled.div`
  padding-left: 12px;
  padding-bottom: 88px;
`;

export const StickyRegionContainer = styled.div`
  background-color: ${colors.white};
  position: sticky;
  top: ${HEADER_HEIGHT_IN_PX}px;
  z-index: 10;
  overflow: hidden;
`;

export const DownloadOptionsContainer = styled.div`
  width: 100%;
  max-width: 1440px;
`;

export const TemplateDetailsTitle = styled.h2`
  font-size: 18px;
  font-weight: 500;
  font-size: 18px;
  line-height: 20px;
  color: ${colors.dark};
  margin-top: 0px;
  margin-bottom: 0px;
`;

export const TableBodyContainer = styled.div`
  border: 1px solid ${colors.mainGray};
  border-top: none;
`;

export const Label = styled.p`
  font-size: 14px;
  margin: ${(props: { margin?: string }) => props.margin};

  b {
    font-weight: 500;
  }
`;

type TemplateTableCellProps = {
  fontSize?: string;
  isLast?: boolean;
  align?: string;
};

export const TemplateTableCell = styled.div`
  font-size: ${(props: TemplateTableCellProps) => props.fontSize};
  line-height: 14px;
  padding: 0px 0px;
  height: 48px;
  display: flex;
  align-items: center;
  text-align: ${(props: TemplateTableCellProps) => props.align};
  box-sizing: border-box;
  border-right: ${(props: TemplateTableCellProps) =>
    !props.isLast ? '1px solid #000' : 'none'};

  &&.white-text {
    color: #fff;
    border-right: ${(props: TemplateTableCellProps) =>
      !props.isLast ? '1px solid #fff' : 'none'};
  }

  div:first-child {
    width: 100%;
  }
`;

export const TemplateHeadCell = styled(TemplateTableCell)`
  padding: 0px 12px;
  justify-content: ${(props: TemplateTableCellProps) => props.align};
`;

export const TemplateTableHeaderContainer = styled.div`
  z-index: 100;
  border: 1px solid ${colors.mainGray};
  border-bottom: none;
`;

export const TemplateTableHead = styled.div`
  background-color: ${(props: { backgroundColor?: string }) =>
    props.backgroundColor};
`;

export const TemplateTableRow = styled.div`
  display: grid;
  grid-template-columns: 4.63fr 36.48fr 7.96fr 7.96fr 11.67fr 11.67fr 11.67fr 7.96fr;
  background-color: ${(props: { backgroundColor?: string }) =>
    props.backgroundColor};
  border-bottom: 1px solid ${colors.mainGray};

  :nth-last-child(1) {
    border-bottom: none;
  }
`;

export const TemplateRowTitle = styled.span`
  width: 170px;
  font-size: 14px;

  &&.bold {
    font-weight: 700;
    font-size: 12px;
  }
`;

export const TemplateSubHeaderCell = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-evenly;

  span {
    font-size: 12px;
    font-weight: normal;
  }

  .open-closed-rows {
    width: 50%;
    text-align: center;
  }
`;

export const TitleCellLabelsContainer = styled.div`
  margin-left: 16px;

  h5 {
    font-size: 14px;
    font-weight: 400;
    margin: 0;
    line-height: 20px;
  }

  h6 {
    font-size: 12px;
    font-weight: 400;
    margin: 0;
  }
`;

export const TemplateTitleContainer = styled.div`
  display: flex;
  align-items: center;

  input {
    padding: 8px 16px;
  }
`;

export const AbsolutePositionedLoader = styled.div`
  position: fixed;
  left: 0;
  right: 0;
  bottom: 0;
  top: 0;
  background-color: #ffffffa0;
`;

export const DownloadProgressMessage = styled.div`
  && {
    padding: 4px 12px 4px 12px;
    border-radius: 4px;
    height: 32px;
    background: ${colors.statusIndicatorsBackground};
    display: flex;
    font-weight: 400;
    font-size: 12px;
    line-height: 16px;
  }
`;

export const DownloadProgressText = styled.div`
  && {
    font-family: 'Roboto';
    font-style: normal;
    font-weight: 400;
    font-size: 12px;
    display: flex;
    align-items: center;
    margin-right: 12px;

    p {
      margin: 0px;
    }
  }
`;

export const DownloadProgressIcon = styled.div`
  && {
    display: flex;
    align-items: center;
  }
`;

export const PathArrow = styled.div`
  && {
    margin: 0px 10px;
  }
`;

export const DownloadsLink = styled(Link)`
  font-family: 'Roboto';
  font-style: normal;
  font-weight: 500;
  font-size: 18px;
  line-height: 20px;
  color: ${colors.mainGray};
`;

export const DownloadMessageContainer = styled.div`
  && {
    background: #fff5e6;
    border: 1px solid #ff9800;
    box-shadow: 0px 2px 4px rgba(0, 0, 0, 0.2);
    border-radius: 4px;
    position: absolute;
    right: 128px;
    z-index: 1300;
    min-width: 474px;
    padding: 12px 16px;
    font-style: normal;
    font-weight: 400;
    font-size: 14px;
    line-height: 20px;
    box-sizing: border-box;
    cursor: move;
  }
`;

export const DownloadMessageTitle = styled.h3`
  display: flex;
  margin: 0px;
  font-family: Roboto;
  font-style: normal;
  font-weight: 500;
  font-size: 16px;
  line-height: 20px;
`;

export const TemplateSelectFieldValue = styled.div`
  .template-name {
    font-weight: 400;
    font-size: 14px;
    line-height: 20px;
  }

  .template-owner {
    font-weight: 400;
    font-size: 12px;
    line-height: 16px;
    color: ${colors.fontColor};
  }
`;

export const CloseIcon = styled.img`
  cursor: pointer;
  height: 18px;
  width: 18px;
`;

export const DownloadErrorMessageLink = styled(Link)`
  && {
    text-decoration: underline;

    &:focus,
    &:hover,
    &:visited,
    &:link,
    &:active {
      color: ${colors.black};
    }
  }
`;

export const RetryButton = styled.button`
  background-color: ${colors.white};
  display: flex;
  height: 36px;
  width: 96px;
  align-items: center;
  justify-content: center;
  border: 1px solid #e0e0e0;
  border-radius: 3px;
  margin-left: 24px;
  margin-right: 28px;
  cursor: pointer;

  .text {
    font-family: Roboto;
    font-weight: 500;
    font-size: 14px;
    line-height: 16px;
    margin-left: 6px;
  }
`;

export const ExpandIconPlaceholder = styled.span`
  width: 44px;
  display: flex;
  align-items: center;
  justify-content: center;
`;

export const CancelButton = styled(SPWButtonAsText)`
  && {
    margin-left: 20px;
    background: ${colors.white};
    border: 1px solid ${colors.veryLightGray};
    border-radius: 3px;
  }
`;
